import type { IAuthorizationItem } from 'src/types/authorization';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Tooltip } from '@mui/material';

import {
  authorizationMatchesActivityCompetencyIds,
  getAuthorizationName,
} from 'src/utils/competency';
import type { CompetencyHierarchy } from 'src/utils/competency-hierarchy';

import { TagsList, TagsListTooltipContent, type Tag } from '../tags-list';

interface Props {
  authorizations?: IAuthorizationItem[];
  activityCompetencyIds?: number[];
  accreditationBodyIds?: number[];
  containerIdentifier: string;
  competencyHierarchy?: CompetencyHierarchy;
  /** Show all activity-matching authorizations wrapped inline; other authorizations only in a tooltip. */
  wrapActiveWithInactiveTooltip?: boolean;
}

export function getActiveAuthorizations(
  authorizations: IAuthorizationItem[] | undefined,
  activityCompetencyIds?: number[],
  accreditationBodyIds?: number[],
  competencyHierarchy?: CompetencyHierarchy,
): IAuthorizationItem[] {
  if (!authorizations?.length) {
    return [];
  }

  if (activityCompetencyIds == null) {
    return authorizations;
  }

  if (activityCompetencyIds.length === 0) {
    return [];
  }

  return authorizations.filter((authorization) => {
    const activityMatch = authorizationMatchesActivityCompetencyIds(
      authorization,
      activityCompetencyIds,
      competencyHierarchy,
    );

    const accreditationBodyIdsLength = accreditationBodyIds?.length ?? 0;
    const accreditationBodyMatch =
      accreditationBodyIdsLength === 0
        ? true
        : !authorization?.accreditationBody
          ? true
          : typeof authorization?.accreditationBody?.id === 'number'
            ? accreditationBodyIds?.includes(authorization.accreditationBody.id)
            : false;

    return activityMatch && accreditationBodyMatch;
  });
}

export const AuthorizationsTooltip = ({
  authorizations,
  activityCompetencyIds,
  accreditationBodyIds,
  containerIdentifier,
  competencyHierarchy,
  wrapActiveWithInactiveTooltip = false,
}: Props) => {
  const { i18n } = useTranslation('organization');

  const activeAuthorizations = useMemo(
    () =>
      getActiveAuthorizations(
        authorizations,
        activityCompetencyIds,
        accreditationBodyIds,
        competencyHierarchy,
      ),
    [authorizations, activityCompetencyIds, accreditationBodyIds, competencyHierarchy]
  );

  const activeAuthorizationIds = useMemo(
    () => new Set(activeAuthorizations.map((authorization) => authorization.id)),
    [activeAuthorizations]
  );

  const activeTags: Tag[] = useMemo(
    () =>
      activeAuthorizations.map((authorization) => ({
        id: authorization.id,
        label: getAuthorizationName(authorization, i18n),
        color: 'info',
        variant: 'filled',
      })),
    [activeAuthorizations, i18n]
  );

  const tooltipTags: Tag[] = useMemo(
    () =>
      [...(authorizations ?? [])]
        .sort((a, b) => {
          const aIsActive = activeAuthorizationIds.has(a.id);
          const bIsActive = activeAuthorizationIds.has(b.id);

          if (aIsActive === bIsActive) {
            return 0;
          }

          return aIsActive ? -1 : 1;
        })
        .map((authorization) => ({
          id: authorization.id,
          label: getAuthorizationName(authorization, i18n),
          color: activeAuthorizationIds.has(authorization.id) ? 'info' : 'default',
          variant: activeAuthorizationIds.has(authorization.id) ? 'filled' : 'soft',
        })),
    [authorizations, activeAuthorizationIds, i18n]
  );

  const inactiveTags: Tag[] = useMemo(
    () =>
      (authorizations ?? [])
        .filter((authorization) => !activeAuthorizationIds.has(authorization.id))
        .map((authorization) => ({
          id: authorization.id,
          label: getAuthorizationName(authorization, i18n),
          color: 'default' as const,
          variant: 'soft' as const,
        })),
    [authorizations, activeAuthorizationIds, i18n]
  );

  if (!authorizations?.length) {
    return null;
  }

  if (wrapActiveWithInactiveTooltip) {
    const inlineContent = (
      <Box id={containerIdentifier} sx={{ minWidth: 0, width: '100%' }}>
        {activeTags.length > 0 ? (
          <TagsList
            containerIdentifier={containerIdentifier}
            tags={activeTags}
            color="info"
            variant="filled"
            wrap
          />
        ) : null}
      </Box>
    );

    if (inactiveTags.length === 0) {
      return inlineContent;
    }

    return (
      <Tooltip
        placement="left-start"
        slotProps={{
          tooltip: {
            sx: (theme) => ({
              maxWidth: 360,
              p: 0,
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: `solid 1px ${theme.palette.divider}`,
              boxShadow: theme.customShadows.dropdown,
            }),
          },
        }}
        title={
          <TagsListTooltipContent
            tags={[
              ...activeTags.map((tag) => ({ ...tag, color: 'info' as const })),
              ...inactiveTags.map((tag) => ({ ...tag, color: 'default' as const })),
            ]}
          />
        }
      >
        <Box sx={{ minWidth: 0, width: '100%', cursor: inactiveTags.length ? 'help' : undefined }}>
          {inlineContent}
        </Box>
      </Tooltip>
    );
  }

  const inlineTags = activeTags.length > 0 ? activeTags : tooltipTags;

  return (
    <Box id={containerIdentifier} sx={{ minWidth: 0, width: '100%', height: '100%' , maxHeight: 24 }}>
      <TagsList
        containerIdentifier={containerIdentifier}
        tags={inlineTags}
        color="info"
        variant="filled"
        overflowColor="default"
        overflowVariant="soft"
        showTooltip
        tooltipContent={<TagsListTooltipContent tags={tooltipTags} />}
      />
    </Box>
  );
};
